import { apiSlice } from "../stores/base/api-slice";
import { resolveUtilityName } from "../tenant/branding/resolveTenant";
import { toQueryString, toQueryStringWithArray } from ".";
import { StaffReferralSingleRequest, UpdatePhoneNumberRequest, UpdateProfileDetailsRequest, UserResponse } from "./contract";
import { PaginatedStaffReferral, StaffReferralRequest } from "../model/referral/referral";

export interface Avatar {
    id: number;
    imageUrl: string;
}

export interface UserFilterOption {
    id: string;
    displayName: string;
    email: string;
}

export interface UsersForFilterResponse {
    customers: UserFilterOption[];
    staff: UserFilterOption[];
}




const userEndpoints = apiSlice.injectEndpoints({
    endpoints: builder => ({
        postHelpMessage: builder.mutation<{ status: string }, any>({
            query: request => ({
                url: `/user/need-help`,
                method: "POST",
                body: request,
            }),
        }),

        confirmProviderRegistration: builder.mutation<any, any>({
            query: (data) => ({
                url: '/user/provider/finalize-provider-registration',
                method: 'POST',
                body: data
            })
        }),

        generateEmailVerificationCode: builder.mutation<any, any>({
            query: (data) => ({
                url: '/user/provider/start-provider-registration',
                method: 'POST',
                body: data
            })
        }),

        completeUtilAdminReg: builder.mutation<any, any>({
            query: (data) => ({
                url: '/user/utility-users/registration/complete',
                method: 'POST',
                body: { ...data, utilityName: resolveUtilityName() }
            })
        }),

        getUserInfo: builder.query<any, void>({
            query: () => 'user/users/get-user-info',
            providesTags: ["UserProfile"]
        }),
        initiateResetTransactionPin: builder.mutation<any, any>({
            query: (data) => ({
                url: '/user/transaction-pin/initiate-reset-pin',
                method: 'POST',
                body: data
            })
        }),
        completeResetTransactionPin: builder.mutation<{pin: string, token: string}, any>({
            query: (data) => ({
                url: '/user/transaction-pin/finalize-reset-pin',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ["UserProfile"],
        }),
        updateFullName: builder.mutation<UserResponse, UpdateProfileDetailsRequest>({
            query: (data) => ({
                url: '/user/users/update-full-name',
                method: 'PUT',
                body: data
            })
        }),
        updateOrganizationName: builder.mutation<UserResponse, { organizationName: string }>({
            query: (data) => ({
                url: '/user/users/update-organization-name',
                method: 'PUT',
                body: data
            }),
            invalidatesTags: ["UserProfile"],
        }),
        updatePhoneNumber: builder.mutation<UserResponse, UpdatePhoneNumberRequest>({
            query: (data) => ({
                url: '/user/users/update-phone-number',
                method: 'PUT',
                body: data
            })
        }),
        completeUpdatePhoneNumber: builder.mutation<UserResponse, string>({
            query: (data) => ({
                url: `/user/users/complete-update-phone-number?code=${data}`,
                method: 'POST',
                body: data
            })
        }),
        deleteUser: builder.mutation<string, void>({
            query: () => ({
                url: '/user/deletion',
                method: 'DELETE'
            })
        }),
        getUsersForFilter: builder.query<UsersForFilterResponse, { departmentId?: string; search?: string }>({
            query: params => ({
                url: `user/users/for-filter${toQueryStringWithArray(params)}`,
                method: "GET",
            }),
        }),

        getAvatars: builder.query<Avatar[], void>({
            query: () => ({
                url: `/user/users/get-avatars`,
                method: "GET",
            }),
        }),

        setAlphabeticalUserPhoto: builder.mutation<void, void>({
            query: () => ({
                url: `/user/users/set-aplphabetical-user-photo`,
                method: "POST",
            }),
            invalidatesTags: ["UserProfile"],
        }),

        updateAvatarImage: builder.mutation<void, { avatarId: number }>({
            query: (body) => ({
                url: `/user/users/update-avatar-image`,
                method: "POST",
                body,
            }),
            invalidatesTags: ["UserProfile"],
        }),

        updateUserPhoto: builder.mutation<void, FormData>({
            query: (body) => ({
                url: `/user/users/update-user-photo`,
                method: "POST",
                body,
            }),
            invalidatesTags: ["UserProfile"],
        }),

        setPreferredLanguage: builder.mutation<void, { language: string }>({
            query: (data) => ({
                url: '/user/users/set-preferred-language',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ["UserProfile"],
        }),


    })
});

export const {
    usePostHelpMessageMutation,
    useConfirmProviderRegistrationMutation,
    useGenerateEmailVerificationCodeMutation,
    useCompleteUtilAdminRegMutation,
    useLazyGetUserInfoQuery,
    useGetUserInfoQuery,
    useInitiateResetTransactionPinMutation,
    useCompleteResetTransactionPinMutation,
    useUpdateFullNameMutation,
    useUpdateOrganizationNameMutation,
    useUpdatePhoneNumberMutation,
    useCompleteUpdatePhoneNumberMutation,
    useDeleteUserMutation,
    useLazyGetUsersForFilterQuery,
    useGetAvatarsQuery,
    useSetAlphabeticalUserPhotoMutation,
    useUpdateAvatarImageMutation,
    useUpdateUserPhotoMutation,
    useSetPreferredLanguageMutation,
} = userEndpoints;
